import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../Components/Button";
import {
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context/businessTransaction/context";
import {
  clearBusinessPartner,
  clearBusinessPartnerError,
  requestCreateBusinessPartner,
  requestGetBusinessTransaction,
  requestListBusinessPartner,
  requestUpdateBusinessTransaction,
} from "../../Context";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { Alert } from "../../Components/Alert";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { NumericFormat } from "react-number-format";
import { useNavigate, useParams } from "react-router-dom";
import InputSkeleton from "../../Components/InputSkeleton";
import FormLabel from "../../Components/FormLabel";
import Select from "react-select";
import { cancelAPIRequest, classNames, getErrorMessages } from "../../Utils";
import { Modal } from "../../Components/Modal";
import { RefreshIcon } from "@heroicons/react/outline";

export const CashBookDetail = () => {
  const [businessId, setbusinessId] = useState("");
  const [transactionID, settransactionID] = useState("");
  const [date, setdate] = useState(new Date());
  const [income, setincome] = useState({ value: "" });
  const [expense, setexpense] = useState({ value: "" });
  const [note, setnote] = useState("");
  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const [fieldError, setfieldError] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const [selectedPartner, setselectedPartner] = useState(null);
  const [showPartnerModal, setshowPartnerModal] = useState(false);
  const [partnerName, setpartnerName] = useState("");
  const [businessType, setbusinessType] = useState("");
  const [partnerNameError, setpartnerNameError] = useState(null);
  const [partnerTypeError, setpartnerTypeError] = useState(null);
  const [partnerSubmitted, setpartnerSubmitted] = useState(false);

  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const dispatch = useBusinessTransactionDispatch();
  const {
    request: businessPartnerRequest,
    updateTransaction,
    loading,
    error,
    transaction,
    businessPartners,
    businessPartnerLoading,
    businessPartnerError,
    newPartner,
    newPartnerLoading,
    newPartnerError,
  } = useBusinessTransactionState();

  const businessTypes = useMemo(() => {
    return [
      { label: "Personal", value: "PERSONAL" },
      { label: "Incorporated", value: "BUSINESS" },
    ].map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }, [t]);

  useEffect(() => {
    if (businessPartnerRequest) {
      cancelAPIRequest(businessPartnerRequest);
    }
    if (businessId) {
      requestListBusinessPartner(dispatch, {
        page: 1,
        businessID: businessId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    if (newPartner) {
      setshowSuccessModal(true);
      if (newPartner.id && newPartner.name) {
        setselectedPartner({
          label: newPartner.name,
          value: newPartner.id,
        });
      }
    }
  }, [newPartner]);

  useEffect(() => {
    if (newPartnerError) {
      setShowErrorModal(true);
    }
  }, [newPartnerError]);

  useEffect(() => {
    if (partnerSubmitted) {
      const isNameValid = partnerName.length >= 2;
      if (isNameValid) {
        setErrorMessage("partnerName", "");
      } else {
        setErrorMessage(
          "partnerName",
          "Partner name must be at least 2 characters long."
        );
      }
    }
  }, [partnerName, partnerSubmitted]);

  useEffect(() => {
    if (partnerSubmitted && businessType.value) {
      const isTypeValid = businessTypes.some(
        (type) => type.value === businessType.value
      );

      if (isTypeValid) {
        setErrorMessage("partnerType", "");
      } else {
        setErrorMessage("partnerType", "Select valid business type.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessType.value, partnerSubmitted]);

  useEffect(() => {
    if (updateTransaction?.id) {
      setshowSuccessModal(true);
    }
  }, [updateTransaction.id]);

  useEffect(() => {
    if (id) {
      requestGetBusinessTransaction(dispatch, { id });
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (transaction) {
      const { id, businessID, createdAt, income, expense, note, partnerID } =
        transaction;

      if (id) settransactionID(id.substr(id.length - 5));
      if (businessID) setbusinessId(businessID);
      if (createdAt) setdate(new Date(createdAt));
      if (income) setincome({ value: income });
      if (expense) setexpense({ value: expense });
      if (note) setnote(note);
      if (partnerID && businessPartners && !newPartner) {
        const selectedPartner = businessPartners.find(
          (item) => item.id === partnerID
        );
        setselectedPartner({
          label: selectedPartner?.name,
          value: selectedPartner?.id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction, businessPartners]);

  const validatePartnerInput = () => {
    let currentValidPartnerName = true;
    let currentValidType = true;

    const isNameValid = partnerName.length >= 2;
    const isTypeValid = businessTypes.some(
      (type) => type.value === businessType.value
    );

    currentValidPartnerName = isNameValid;

    if (businessType) {
      currentValidType = isTypeValid;
    }

    return currentValidPartnerName && currentValidType;
  };

  const onSave = () => {
    if (id && businessId && date && (income.value || expense.value)) {
      setfieldError(false);

      const transactionData = {
        id,
        type: "SALE",
        createdAt: date.toISOString(),
        income: income.value || 0,
        expense: expense.value || 0,
        note,
      };

      if (selectedPartner?.value) {
        transactionData.partnerID = selectedPartner.value;
      } else {
        transactionData.partnerID = null;
      }

      requestUpdateBusinessTransaction(dispatch, {
        input: transactionData,
      });
    } else {
      setfieldError(true);
    }
  };

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "partnerName":
        setpartnerNameError(message);
        break;
      case "partnerType":
        setpartnerTypeError(message);
        break;
      default:
        break;
    }
  };

  const handlePartnerSave = () => {
    setpartnerSubmitted(true);
    if (validatePartnerInput()) {
      const input = {
        businessID: businessId,
        name: partnerName,
        type: businessType.value,
      };
      requestCreateBusinessPartner(dispatch, { input });
    }
  };

  const navigateToTransaction = () => {
    setshowSuccessModal(false);
    if (!error) {
      if (updateTransaction?.id) {
        navigate(-1);
      }
    }
  };

  const resetPartnerModal = () => {
    setpartnerName("");
    setbusinessType("");
    setpartnerNameError("");
    setpartnerTypeError("");
  };

  const renderPartnerModal = () => {
    return (
      <Modal
        title={t("Add Partner")}
        show={showPartnerModal}
        onClose={() => {
          setshowPartnerModal(false);
          setpartnerSubmitted(false);
          resetPartnerModal();
        }}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button
              disabled={newPartnerLoading}
              loading={newPartnerLoading}
              onClick={(e) => {
                e.preventDefault();
                handlePartnerSave();
              }}
              className="w-24 shrink-0"
              title="Save"
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 px-4 py-5">
          {newPartnerError && (
            <ModalConfirm
              open={showErrorModal}
              setOpen={(open) => {
                setShowErrorModal(open);
              }}
              title="Error"
              description={
                (newPartnerError?.response?.body &&
                  JSON.parse(newPartnerError.response.body)?.message) ||
                getErrorMessages(newPartnerError)
              }
              type="ALERT"
              onConfirm={() => {
                setShowErrorModal(false);
                clearBusinessPartnerError(dispatch);
              }}
            />
          )}
          {newPartner && (
            <ModalConfirm
              open={showSuccessModal}
              setOpen={(open) => {
                setshowSuccessModal(open);
              }}
              title="Success"
              description={"Your new partner has been created."}
              type="SUCCESS"
              onConfirm={() => {
                setshowPartnerModal(false);
                setshowSuccessModal(false);
                clearBusinessPartner(dispatch);
                setpartnerSubmitted(false);
                resetPartnerModal();
              }}
            />
          )}
          <div>
            <FormLabel htmlFor="name" label={t("Name")} required />
            <input
              type="text"
              name="name"
              value={partnerName}
              placeholder={t("Enter Partner Name")}
              onChange={(e) => setpartnerName(e.target.value)}
              className={classNames(
                partnerNameError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
            />
            {partnerNameError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(partnerNameError)}
              </div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="type" label={t("Business Type")} />
            <Select
              id="type"
              name="type"
              className={classNames(
                partnerTypeError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 text-sm"
              )}
              classNamePrefix="select2-selection"
              options={businessTypes}
              value={businessType}
              onChange={setbusinessType}
              placeholder={t("Select Business Type")}
            />
            {partnerTypeError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(partnerTypeError)}
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  const renderPartnerError = (error) => {
    const messages = getErrorMessages(error);
    return (
      <div>
        <div className="flex justify-between">
          <FormLabel htmlFor="partner" label={t("Partner or Customer")} />
          {businessId && (
            <button
              type="button"
              onClick={() => {
                setshowPartnerModal(true);
                resetPartnerModal();
              }}
              className="text-blue-600 text-sm font-medium"
            >
              {t("Add Partner")}
            </button>
          )}
        </div>
        <div className="relative">
          <input
            type="text"
            name="partner"
            value={messages}
            className="mt-1 pr-3 py-2 w-full text-sm rounded-md shadow-sm border-red-500 text-red-500"
            disabled={businessPartnerError}
          />
          <button
            type="button"
            onClick={() => {
              if (businessId) {
                requestListBusinessPartner(dispatch, {
                  page: 1,
                  businessID: businessId,
                });
              }
            }}
            className="absolute inset-y-0 right-0 px-2 py-1 flex items-center"
          >
            <RefreshIcon className="w-6 h-6 text-blue-500" />
          </button>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={error?.message || "Error update transaction."}
          errors={error?.errors}
        />
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-md sm:rounded-lg shadow-sm">
      {renderPartnerModal()}
      {updateTransaction?.id && (
        <ModalConfirm
          open={showSuccessModal}
          setOpen={(open) => {
            setshowSuccessModal(open);
          }}
          title="Success"
          description="Your transaction has been updated."
          type="SUCCESS"
          onConfirm={navigateToTransaction}
        />
      )}

      <div className="space-y-4 px-4 py-5">
        {fieldError && (
          <Alert
            title="Incomplete Form"
            type="warning"
            message="Please fill out all fields."
          />
        )}
        {loading ? (
          <InputSkeleton rows={6} />
        ) : (
          <>
            <div>
              <label
                htmlFor="business"
                className="block text-sm font-medium text-gray-700"
              >
                {t("ID")}
              </label>
              <input
                className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 bg-gray-100 text-gray-400"
                type="text"
                disabled={true}
                placeholder={t("")}
                value={transactionID}
                onChange={(e) => settransactionID(e.target.value)}
              />
            </div>
            <div>
              <label
                htmlFor="date"
                className="block text-sm font-medium text-gray-700"
              >
                {t("Date")}
              </label>
              <ReactDatePicker
                className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                selected={date}
                onChange={setdate}
                dateFormat="dd-MM-yyyy"
                placeholderText={t("Select Date")}
              />
            </div>
            <div>
              <label
                htmlFor="income"
                className="block text-sm font-medium text-gray-700"
              >
                {t("Sale")}
              </label>
              <NumericFormat
                type="text"
                name="income"
                value={income.value}
                placeholder={t("Enter Sale Transaction")}
                thousandSeparator="."
                decimalSeparator=","
                displayType="input"
                prefix={"Rp"}
                onValueChange={(values) => setincome(values)}
                className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="expense"
                className="block text-sm font-medium text-gray-700"
              >
                {t("Expense")}
              </label>
              <NumericFormat
                type="text"
                name="expense"
                value={expense.value}
                placeholder={t("Enter expense transaction")}
                thousandSeparator="."
                decimalSeparator=","
                displayType="input"
                prefix={"Rp"}
                onValueChange={(values) => setexpense(values)}
                className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="note"
                className="block text-sm font-medium text-gray-700"
              >
                {t("Note")}
              </label>
              <input
                type="text"
                name="note"
                id="note"
                value={note}
                placeholder={t("Enter Transaction Note")}
                onChange={(e) => setnote(e.target.value)}
                className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            {businessPartnerError ? (
              renderPartnerError(businessPartnerError)
            ) : (
              <div>
                <div className="flex justify-between">
                  <FormLabel
                    htmlFor="partner"
                    label={t("Partner or Customer")}
                  />
                  {businessId && (
                    <button
                      type="button"
                      onClick={() => {
                        setshowPartnerModal(true);
                        resetPartnerModal();
                      }}
                      className="text-sm font-medium text-blue-600"
                    >
                      {t("Add Partner")}
                    </button>
                  )}
                </div>
                <Select
                  id="partner"
                  className="mt-1 text-sm"
                  classNamePrefix="select2-selection"
                  options={businessPartners.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  value={selectedPartner}
                  onChange={(selectedOption) => {
                    setselectedPartner(selectedOption);
                  }}
                  placeholder={t("Select Partner or Customer")}
                  maxMenuHeight={150}
                  menuPortalTarget={document.body}
                  isLoading={businessPartnerLoading}
                  isDisabled={businessPartnerLoading}
                  isClearable
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className="bg-gray-50 rounded-b-md sm:rounded-b-lg p-4 text-right">
        <Button
          title={t("Save")}
          loading={loading}
          disabled={loading}
          onClick={onSave}
        />
      </div>
    </div>
  );
};
